var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Pressable, TextInput, Keyboard } from 'react-native';
import { checkIsPlatform, getTestId } from '../../../utils';
import { Typography } from '../../typography';
import { getOtpBoxesStyles, getTextInputOtpStyles } from '../InputOtpStyles';
export var OtpBoxes = forwardRef((props, ref) => {
  var _a;
  var isInitialMount = useRef(true);
  var inputRefs = useRef();
  var _useState = useState(false),
    isKeyboardVisible = _useState[0],
    setIsKeyboardVisible = _useState[1];
  var isDisabled = props.isDisabled,
    state = props.state,
    _props$numberOfInputs = props.numberOfInputs,
    numberOfInputs = _props$numberOfInputs === void 0 ? 4 : _props$numberOfInputs,
    _props$enableiOSOTPSu = props.enableiOSOTPSuggestionFix,
    enableiOSOTPSuggestionFix = _props$enableiOSOTPSu === void 0 ? false : _props$enableiOSOTPSu,
    _defaultValue = props.defaultValue,
    onOtpChange = props.onOtpChange,
    otpBoxContainerProps = props.otpBoxContainerProps,
    _props$otpBoxProps = props.otpBoxProps,
    otpBoxProps = _props$otpBoxProps === void 0 ? {} : _props$otpBoxProps,
    onOtpBoxPress = props.onOtpBoxPress,
    _style = props.style,
    _props$testID = props.testID,
    testID = _props$testID === void 0 ? 'OtpBoxes' : _props$testID;
  var prevNumberOfInputsRef = useRef(numberOfInputs);
  var textStyle = getTextInputOtpStyles(props);
  var defaultValue = (_a = "" + (_defaultValue !== null && _defaultValue !== void 0 ? _defaultValue : '')) === null || _a === void 0 ? void 0 : _a.slice(0, numberOfInputs);
  var _onFocus = otpBoxProps.onFocus,
    _onBlur = otpBoxProps.onBlur,
    restProps = __rest(otpBoxProps, ["onFocus", "onBlur"]);
  var disabledTextInput = isDisabled || state === 'disabled';
  var _useState2 = useState(defaultValue),
    wholeInputValue = _useState2[0],
    setWholeInputValue = _useState2[1];
  useImperativeHandle(ref, () => ({
    clear: () => {
      setWholeInputValue('');
    }
  }));
  useEffect(() => {
    setTimeout(() => {
      var _a;
      (_a = inputRefs === null || inputRefs === void 0 ? void 0 : inputRefs.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, 0);
  }, []);
  useEffect(() => {
    var shouldSkip = checkIsPlatform('ios') && enableiOSOTPSuggestionFix && (wholeInputValue === null || wholeInputValue === void 0 ? void 0 : wholeInputValue.trim().length) > 0;
    if (Number(defaultValue) && !shouldSkip) {
      setWholeInputValue(defaultValue);
    }
  }, [defaultValue]);
  useEffect(() => {
    if (numberOfInputs !== prevNumberOfInputsRef.current) {
      prevNumberOfInputsRef.current = numberOfInputs;
      setWholeInputValue('');
    }
  }, [numberOfInputs]);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      onOtpChange && onOtpChange(wholeInputValue);
    }
  }, [wholeInputValue]);
  useEffect(() => {
    var keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', () => {
      setIsKeyboardVisible(true);
    });
    var keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => {
      setIsKeyboardVisible(false);
    });
    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);
  var otpBoxMarginStyle = useCallback(index => {
    return {
      marginEnd: numberOfInputs - 1 === index ? 0 : 8.6
    };
  }, [numberOfInputs]);
  var onChangeTextHandler = text => {
    text = text.replace(/[^+\d]/g, '');
    setWholeInputValue(text);
  };
  var getOtpBoxesContainerStyles = (event, id) => {
    var isLastIndex = (wholeInputValue === null || wholeInputValue === void 0 ? void 0 : wholeInputValue.length) === numberOfInputs && numberOfInputs - 1 === id;
    var styles = getOtpBoxesStyles(props, Object.assign(Object.assign({}, event), {
      focused: (wholeInputValue === null || wholeInputValue === void 0 ? void 0 : wholeInputValue.length) === id || isLastIndex
    }));
    return styles;
  };
  var otpBoxesArray = new Array(numberOfInputs).fill('');
  return _jsxs(_Fragment, {
    children: [_jsx(TextInput, Object.assign({
      style: textStyle === null || textStyle === void 0 ? void 0 : textStyle.hiddenInput,
      keyboardType: "numeric",
      autoFocus: false,
      maxLength: numberOfInputs,
      onChangeText: text => {
        onChangeTextHandler(text);
      },
      value: wholeInputValue,
      onFocus: event => {
        _onFocus === null || _onFocus === void 0 ? void 0 : _onFocus(Object.assign(Object.assign({}, event), {
          id: wholeInputValue === null || wholeInputValue === void 0 ? void 0 : wholeInputValue.length
        }));
      },
      onBlur: event => {
        _onBlur === null || _onBlur === void 0 ? void 0 : _onBlur(Object.assign(Object.assign({}, event), {
          id: wholeInputValue === null || wholeInputValue === void 0 ? void 0 : wholeInputValue.length
        }));
      },
      ref: inputRefs,
      editable: !disabledTextInput,
      importantForAccessibility: "no",
      accessibilityElementsHidden: true,
      selectTextOnFocus: false,
      pointerEvents: "none"
    }, getTestId(testID, "inputBox"), {
      accessibilityLabel: "Please Enter OTP",
      caretHidden: true
    })), otpBoxesArray === null || otpBoxesArray === void 0 ? void 0 : otpBoxesArray.map((_e, id) => {
      var _a;
      var accessibilityHint = "text in OTP box number " + (id + 1) + " out of " + numberOfInputs + " is " + (((_a = wholeInputValue === null || wholeInputValue === void 0 ? void 0 : wholeInputValue[id]) === null || _a === void 0 ? void 0 : _a.length) > 0 ? wholeInputValue === null || wholeInputValue === void 0 ? void 0 : wholeInputValue[id] : 'empty');
      return _jsx(Pressable, Object.assign({
        style: event => {
          var styles = getOtpBoxesContainerStyles(event, id);
          return [styles.inputOtpWrapper, otpBoxMarginStyle(id), _style];
        },
        onPress: () => {
          var _a, _b, _c, _d, _f, _g;
          if (!((_b = (_a = inputRefs === null || inputRefs === void 0 ? void 0 : inputRefs.current) === null || _a === void 0 ? void 0 : _a.isFocused) === null || _b === void 0 ? void 0 : _b.call(_a))) {
            (_d = (_c = inputRefs === null || inputRefs === void 0 ? void 0 : inputRefs.current) === null || _c === void 0 ? void 0 : _c.focus) === null || _d === void 0 ? void 0 : _d.call(_c);
          }
          if (!isKeyboardVisible) {
            inputRefs.current.blur();
            (_g = (_f = inputRefs === null || inputRefs === void 0 ? void 0 : inputRefs.current) === null || _f === void 0 ? void 0 : _f.focus) === null || _g === void 0 ? void 0 : _g.call(_f);
          }
          onOtpBoxPress === null || onOtpBoxPress === void 0 ? void 0 : onOtpBoxPress(id);
        }
      }, getTestId(testID, "" + id), {
        accessibilityLabel: "OTP",
        accessibilityHint: accessibilityHint
      }, checkIsPlatform('web') && {
        accessibilityLabel: accessibilityHint
      }, otpBoxContainerProps, {
        accessible: true
      }, {
        children: _jsx(Typography, Object.assign({
          style: textStyle.otpTextStyle,
          accessible: false,
          text: wholeInputValue[id] || '',
          type: "h4-bold"
        }, getTestId(testID, "textInput_" + id), restProps))
      }), "single_otp_boxes_" + id);
    })]
  });
});